import React, { useEffect, useRef, useState } from "react";
import { Paperclip, Image, Camera, Video, Search } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";

const EmojiPicker = React.lazy(() => import("emoji-picker-react"));

// ── Allowed file extensions (client-side guard) ───────────────────────────
const ALLOWED_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "gif", "webp", "heic", "heif",
  "mp3", "wav", "m4a", "ogg", "aac",
  "pdf", "doc", "docx", "txt", "zip",
  "mp4", "mov", "avi", "mkv", "webm", "m4v", "3gp",
]);

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";

  const now = new Date();
  const diffMs = now - d;

  // Clock-skew / future timestamps collapse to "just now".
  if (diffMs < 60_000) return "just now";

  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) return `${diffMin}m ago`;

  let h = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  const clock = `${h}:${mm}${ampm}`;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((today - msgDay) / 86_400_000);

  if (dayDiff === 0) return clock;
  if (dayDiff === 1) return `Yesterday ${clock}`;
  if (dayDiff < 7) {
    const wd = d.toLocaleDateString("en-AU", { weekday: "short" });
    return `${wd} ${clock}`;
  }
  const dm = d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  return `${dm} ${clock}`;
}

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isSameDay(isoA, isoB) {
  const a = new Date(isoA);
  const b = new Date(isoB);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function fmtDate(iso) {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((today - msgDay) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

function avatarInitials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

// ── SVG icons (exported for use in TradieDashboard media grid) ────────────────

export function IconFile({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

export function IconAudio({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );
}

export function IconVideo({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}

// ── Upload icon button ─────────────────────────────────────────────────────
// Each button triggers its own hidden file input.
// Hover highlights the icon in orange (#FF8A00).

function UploadBtn({ icon, title, inputRef, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={() => inputRef.current?.click()}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flexShrink: 0,
        width: "36px",
        height: "36px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid var(--border, #e5e7eb)",
        borderRadius: "8px",
        background: "var(--surface, #fff)",
        color: hovered && !disabled ? "var(--primary)" : "var(--text-muted, #6b7280)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "color 0.15s",
        padding: 0,
      }}
    >
      {icon}
    </button>
  );
}


// ── Attachment preview bar ─────────────────────────────────────────────────

function AttachmentPreview({ pending, onCancel }) {
  const { file, previewUrl } = pending;
  const isImage = file.type.startsWith("image/");
  const isAudio = file.type.startsWith("audio/");
  const isVideo = file.type.startsWith("video/");

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "8px 14px",
        borderTop: "1px solid var(--border, #e5e7eb)",
        background: "rgba(255, 138, 0,0.04)",
      }}
    >
      {isImage && previewUrl ? (
        <img
          src={previewUrl}
          alt={file.name}
          style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "8px", flexShrink: 0, border: "1px solid var(--border, #e5e7eb)" }}
        />
      ) : (
        <div
          style={{ width: "44px", height: "44px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.06)", borderRadius: "8px", flexShrink: 0, color: "var(--text-muted, #6b7280)" }}
        >
          {isAudio ? <IconAudio /> : isVideo ? <IconVideo /> : <IconFile />}
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text, #1C1C1E)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {file.name}
        </div>
        <div style={{ fontSize: "0.75rem", color: "var(--text-muted, #6b7280)" }}>
          {fmtBytes(file.size)}
        </div>
      </div>

      <button
        type="button"
        onClick={onCancel}
        style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", color: "var(--text-muted, #6b7280)", fontSize: "1.25rem", lineHeight: 1, padding: "4px" }}
        aria-label="Remove attachment"
      >
        ×
      </button>
    </div>
  );
}

// ── Inline file renderer inside chat bubbles ───────────────────────────────

function FileAttachment({ msg, isMine, onOpenLightbox }) {
  const { file_url, file_name, file_type } = msg;
  if (!file_url) return null;

  const isImage = file_type?.startsWith("image/");
  const isAudio = file_type?.startsWith("audio/");
  const isVideo = file_type?.startsWith("video/");
  const mutedColor = isMine ? "rgba(255,255,255,0.75)" : "var(--text-muted, #6b7280)";
  const linkColor  = isMine ? "#fff" : "var(--primary)";

  if (isImage) {
    // No <a> wrapper — clicking always opens the internal lightbox, never a new tab.
    return (
      <img
        src={file_url}
        alt={file_name || "Image"}
        onClick={(e) => { e.stopPropagation(); onOpenLightbox(msg); }}
        style={{
          maxWidth: "220px",
          maxHeight: "180px",
          borderRadius: "8px",
          display: "block",
          cursor: "pointer",
          marginBottom: msg.body ? "6px" : 0,
        }}
      />
    );
  }

  if (isAudio) {
    return (
      <div style={{ marginBottom: msg.body ? "6px" : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px", color: mutedColor, fontSize: "0.8rem" }}>
          <IconAudio size={18} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "160px" }}>{file_name}</span>
        </div>
        <audio controls src={file_url} style={{ maxWidth: "220px", width: "100%" }} />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div
        style={{ marginBottom: msg.body ? "6px" : 0, position: "relative", cursor: "pointer" }}
        onClick={(e) => { e.stopPropagation(); onOpenLightbox(msg); }}
      >
        <video
          src={file_url}
          style={{ maxWidth: "220px", maxHeight: "160px", borderRadius: "8px", display: "block", pointerEvents: "none" }}
        />
        {/* Play overlay hint */}
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", background: "rgba(0,0,0,0.25)" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#1C1C1E"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          </div>
        </div>
      </div>
    );
  }

  // PDF / other — download link
  return (
    <a
      href={file_url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: msg.body ? "6px" : 0, color: linkColor, textDecoration: "none" }}
    >
      <IconFile size={22} />
      <span style={{ fontSize: "0.85rem", fontWeight: 600, wordBreak: "break-word" }}>{file_name || "File"}</span>
    </a>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

// NOTE: to fix tradie profiles missing business_id in Supabase run:
//   UPDATE profiles SET business_id = id WHERE business_id IS NULL AND role = 'tradie';
//
// `readOnly` hides the composer and disables sending. Used on the customer
// dashboard when a quote has been declined (or the job was awarded to a
// different tradie) — the chat history must stay visible but neither side
// should be able to send new messages from within that locked card.
// `readOnlyReason` is optional copy surfaced above the input area.
export default function LeadChat({ leadId, senderRole, senderName, businessId, isResolving = false, onAddToMedia, markAsRead, readOnly = false, readOnlyReason = null }) {
  // Local search filter — strips the message list down to bubbles whose
  // body or sender_name contains the query (case-insensitive). Empty
  // query = show all (no perf hit).
  const [chatSearch, setChatSearch] = React.useState("");
  const { showToast } = useToast();

  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [lightboxItem, setLightboxItem] = useState(null); // msg object
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // True for ~3s after the OTHER party sends a typing broadcast — drives
  // the "typing…" indicator below the message list.
  const [otherTyping, setOtherTyping] = useState(false);
  const bottomRef = useRef(null);
  const channelRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  // Mark lead as read whenever the active lead changes
  useEffect(() => { markAsRead?.(leadId); }, [leadId, markAsRead]);

  // ── Close emoji picker on outside click ──────────────────────────────────
  useEffect(() => {
    if (!showEmojiPicker) return;
    function handleOutside(e) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showEmojiPicker]);

  function handleEmojiClick(emojiData) {
    setBody((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  }

  // Broadcast a typing event at most once every 1.5s while the user is
  // actively typing. The receiver auto-clears its indicator after 3s of
  // silence, so this throttle keeps the indicator alive while typing
  // without flooding the channel with one event per keystroke.
  function broadcastTyping() {
    const channel = channelRef.current;
    if (!channel) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 1500) return;
    lastTypingSentRef.current = now;
    channel.send({
      type: "broadcast",
      event: "typing",
      payload: { role: senderRole },
    });
  }

  // ── Load + subscribe ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!leadId) return;

    // Block fetch and subscription until businessId is resolved — prevents
    // accidental cross-tradie leakage and broken empty-state flashes.
    if (!businessId) {
      setMessages([]);
      return;
    }

    let fetchQuery = supabase
      .from("lead_messages")
      .select("*")
      .eq("lead_id", leadId)
      .or(`business_id.eq.${businessId},business_id.is.null,sender_role.eq.customer`)
      .order("created_at", { ascending: true });
    fetchQuery.then(async ({ data }) => {
      if (!data) return;
      setMessages(data);
      // Mark every message I've now seen (sent by the OTHER party, not yet
      // read) as read. The sender's UI will pick up the UPDATE via the
      // realtime channel below and show its "Seen" indicator.
      const unread = data
        .filter((m) => m.sender_role !== senderRole && !m.read_at)
        .map((m) => m.id);
      if (unread.length > 0) {
        await supabase
          .from("lead_messages")
          .update({ read_at: new Date().toISOString() })
          .in("id", unread);
      }
    });

    const channel = supabase
      .channel(`lead-messages-${leadId}-${businessId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "lead_messages", filter: `lead_id=eq.${leadId}` }, async (payload) => {
        const m = payload.new;
        if (!m?.id || m.lead_id !== leadId) return;
        if (m.business_id && m.business_id !== businessId && m.sender_role !== "customer") return; // business_id===null passes through (legacy messages)
        setMessages((prev) => {
          if (prev.some((existing) => existing.id === m.id)) return prev;
          return [...prev, m];
        });
        // If a fresh message arrives from the other party while this thread
        // is open, mark it read straight away so the sender sees "Seen"
        // without waiting for me to refresh.
        if (m.sender_role !== senderRole && !m.read_at) {
          await supabase
            .from("lead_messages")
            .update({ read_at: new Date().toISOString() })
            .eq("id", m.id);
        }
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "lead_messages", filter: `lead_id=eq.${leadId}` }, (payload) => {
        const m = payload.new;
        if (!m?.id || m.lead_id !== leadId) return;
        // Patch in-place — used to surface the recipient's read_at on the
        // sender's UI for the "Seen" indicator.
        setMessages((prev) => prev.map((existing) => existing.id === m.id ? { ...existing, ...m } : existing));
      })
      .on("broadcast", { event: "typing" }, (payload) => {
        // Broadcast events come through with shape { event, payload, type }.
        // We track typing by sender_role so the customer never sees their
        // own typing indicator and vice versa.
        const fromRole = payload?.payload?.role;
        if (!fromRole || fromRole === senderRole) return;
        setOtherTyping(true);
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setOtherTyping(false), 3000);
      })
      .subscribe();

    channelRef.current = channel;
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      supabase.removeChannel(channel);
    };
  }, [leadId, businessId, senderRole]);

  // ── Auto-scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (messages.length === 0) return;
    const chatContainer = bottomRef.current?.closest('.chat-messages');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }, [messages]);

  // ── File selection ───────────────────────────────────────────────────────
  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_EXTENSIONS.has(ext)) return;
    const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
    setPendingFile({ file, previewUrl });
  }

  function cancelPending() {
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    setPendingFile(null);
  }

  // ── Send ─────────────────────────────────────────────────────────────────
  async function sendMessage() {
    if (!businessId) return; // no conversation context yet
    if (readOnly) return; // locked thread — ignore send attempts
    const text = body.trim();
    if (!text && !pendingFile) return;
    if (sending) return;

    setSending(true);
    const savedBody = text;
    const savedFile = pendingFile;
    setBody("");
    setPendingFile(null);
    if (savedFile?.previewUrl) URL.revokeObjectURL(savedFile.previewUrl);

    let filePayload = {};

    if (savedFile) {
      const { file } = savedFile;
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${leadId}/${timestamp}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("chat-media")
        .upload(path, file, { upsert: false });

      if (!uploadError) {
        const { data: urlData } = supabase.storage.from("chat-media").getPublicUrl(path);
        filePayload = {
          file_url: urlData?.publicUrl || null,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
        };
      }
    }

    const { data: insertedMsg, error } = await supabase
      .from("lead_messages")
      .insert({
        lead_id: leadId,
        sender_role: senderRole,
        sender_name: senderName,
        body: savedBody,
        business_id: businessId || null,
        ...filePayload,
      })
      .select()
      .single();

    if (error) {
      // eslint-disable-next-line no-console
      console.error("[LeadChat] Message insert failed:", error.message, "| code:", error.code, "| details:", error.details, "| hint:", error.hint);
      showToast(`Failed to send message: ${error.message}`, "error");
      setBody(savedBody);
      if (savedFile) setPendingFile(savedFile);
    } else {
      // eslint-disable-next-line no-console
      console.log("[LeadChat] Message inserted:", insertedMsg?.id);
      // Optimistic update — show the message immediately without waiting for Realtime.
      // The Realtime handler deduplicates by id so it will skip this when it fires.
      setMessages((prev) => {
        if (prev.find((m) => m.id === insertedMsg.id)) return prev;
        return [
          ...prev,
          {
            id: insertedMsg.id,
            lead_id: leadId,
            sender_role: senderRole,
            sender_name: senderName,
            body: savedBody,
            business_id: businessId || null,
            file_url:  filePayload.file_url  || null,
            file_name: filePayload.file_name || null,
            file_type: filePayload.file_type || null,
            file_size: filePayload.file_size || null,
            created_at: insertedMsg.created_at || new Date().toISOString(),
          },
        ];
      });
      // Fire-and-forget email notification to the "other party" so they see
      // the message even when the app isn't open. Non-blocking — in-app chat
      // keeps working even if Resend is offline or the function 500s.
      //
      // NOTE on error handling: supabase.functions.invoke RESOLVES with
      // `{ data, error }` for non-2xx responses — it does NOT reject. The
      // old `.catch(() => {})` only caught actual network exceptions, which
      // meant function errors (JWT rejection, Resend failure, bad payload)
      // disappeared silently. We now log both paths so devtools shows the
      // real cause when notifications stop working.
      supabase.functions
        .invoke("send-message-email", {
          body: {
            leadId,
            senderRole,
            senderName,
            body: savedBody,
            appUrl: window.location.origin,
          },
        })
        .then(({ data, error: fnError }) => {
          if (fnError) {
            // eslint-disable-next-line no-console
            console.warn("[send-message-email] invoke error:", fnError, data);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("[send-message-email] network error:", err);
        });
    }

    setSending(false);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const canSend = (body.trim() || pendingFile) && !sending && !!businessId && !readOnly;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes rqp-typing-dot {
          0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
          40%           { opacity: 1;   transform: translateY(-2px); }
        }
      `}</style>
      <div
        style={{
          border: "1px solid var(--hairline, var(--border, #e5e7eb))",
          borderRadius: "12px",
          overflow: "hidden",
          background: "var(--surface-2, var(--surface, #fff))",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "440px",
        }}
      >
        {/* ── Search bar (filters messages locally) ─────────────── */}
        <div
          className="rq-input"
          style={{
            margin: "10px 12px 0",
            padding: "6px 10px",
            background: "var(--surface-3, var(--surface, #fafafa))",
            borderRadius: "10px",
          }}
        >
          <Search size={14} style={{ color: "var(--text-3, #8a7468)", flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search this conversation…"
            value={chatSearch}
            onChange={(e) => setChatSearch(e.target.value)}
            aria-label="Search conversation"
          />
          {chatSearch && (
            <button
              type="button"
              onClick={() => setChatSearch("")}
              style={{
                background: "none",
                border: "none",
                color: "var(--text-3, #8a7468)",
                cursor: "pointer",
                fontSize: "0.85rem",
                padding: "0 2px",
                flexShrink: 0,
              }}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {/* Hidden file inputs — one per button type */}
        <input ref={fileInputRef}   type="file" accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/zip,video/*" style={{ display: "none" }} onChange={handleFileChange} />
        <input ref={imageInputRef}  type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleFileChange} />
        <input ref={videoInputRef}  type="file" accept="video/*" capture="environment" style={{ display: "none" }} onChange={handleFileChange} />

        {/* Message list */}
        <div className="chat-messages">
          {(() => {
            const visibleMessages = chatSearch.trim()
              ? messages.filter((m) => {
                  const q = chatSearch.toLowerCase();
                  return (
                    (m.body || "").toLowerCase().includes(q) ||
                    (m.sender_name || "").toLowerCase().includes(q)
                  );
                })
              : messages;
            if (visibleMessages.length === 0) {
              return (
                <p className="chat-empty">
                  {isResolving
                    ? "Loading…"
                    : chatSearch.trim()
                    ? `No messages match “${chatSearch}”`
                    : "No messages yet — start the conversation."}
                </p>
              );
            }
            return (() => {
            // Find the most recent of MY messages that has been read by the
            // recipient — the "Seen" indicator goes under that one only.
            let lastSeenMineIndex = -1;
            visibleMessages.forEach((m, i) => {
              if (m.sender_role === senderRole && m.read_at) lastSeenMineIndex = i;
            });
            return visibleMessages.map((msg, index) => {
              const isMine = msg.sender_role === senderRole;
              const showSeen = isMine && index === lastSeenMineIndex;
              const prev = visibleMessages[index - 1];
              const next = visibleMessages[index + 1];
              const groupedTop = !!(prev && prev.sender_role === msg.sender_role && isSameDay(prev.created_at, msg.created_at));
              const groupedBottom = !!(next && next.sender_role === msg.sender_role && isSameDay(next.created_at, msg.created_at));
              const showDate = !prev || !isSameDay(prev.created_at, msg.created_at);
              const bubbleCls = [
                "chat-bubble",
                isMine ? "chat-bubble--mine" : "chat-bubble--theirs",
                groupedTop ? "chat-bubble--grouped-top" : "",
                groupedBottom ? "chat-bubble--grouped-bottom" : "",
              ].filter(Boolean).join(" ");
              const rowCls = [
                "chat-msg-row",
                isMine ? "chat-msg-row--mine" : "",
                groupedTop ? "chat-msg-row--grouped-top" : "",
              ].filter(Boolean).join(" ");
              return (
                <React.Fragment key={msg.id}>
                  {showDate && (
                    <div className="chat-date-sep">
                      <span>{fmtDate(msg.created_at)}</span>
                    </div>
                  )}
                  <div className={rowCls}>
                    {!isMine && (
                      groupedBottom
                        ? <div className="chat-avatar--spacer" />
                        : <div className="chat-avatar">{avatarInitials(msg.sender_name)}</div>
                    )}
                    <div className={isMine ? "chat-meta chat-meta--mine" : "chat-meta"}>
                      <div className={bubbleCls}>
                        <FileAttachment msg={msg} isMine={isMine} onOpenLightbox={setLightboxItem} />
                        {msg.body && <span>{msg.body}</span>}
                      </div>
                      {!groupedBottom && (
                        <span className="chat-meta-line">
                          {msg.sender_name} · {fmtTime(msg.created_at)}
                        </span>
                      )}
                      {showSeen && (
                        <span className="chat-seen">Seen</span>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            });
          })();
          })()}
          {otherTyping && (
            <div className="chat-typing">
              typing
              <span className="chat-typing-dots">
                <span className="chat-typing-dot" />
                <span className="chat-typing-dot" />
                <span className="chat-typing-dot" />
              </span>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Attachment preview bar */}
        {pendingFile && !readOnly && <AttachmentPreview pending={pendingFile} onCancel={cancelPending} />}

        {/* Read-only footer — replaces the composer when the thread is locked
            (quote declined, job awarded elsewhere, etc.) so the history stays
            readable but neither party can send new messages from this card. */}
        {readOnly && (
          <div
            style={{
              padding: "10px 14px",
              borderTop: "1px solid var(--border, #e5e7eb)",
              background: "rgba(0,0,0,0.04)",
              color: "var(--text-muted, #6b7280)",
              fontSize: "0.82rem",
              textAlign: "center",
            }}
          >
            {readOnlyReason || "This conversation is read-only."}
          </div>
        )}

        {/* Input row: icon grid | textarea+emoji | send */}
        {!readOnly && (
        <div className="chat-input-row">
          <div className="chat-input-icons">
            <UploadBtn icon={<Paperclip size={16} />} title="Attach a file (PDF, Word, ZIP etc)"  inputRef={fileInputRef}   disabled={sending} />
            <UploadBtn icon={<Image     size={16} />} title="Upload a photo from your library"     inputRef={imageInputRef}  disabled={sending} />
            <UploadBtn icon={<Camera    size={16} />} title="Take a photo with your camera"        inputRef={cameraInputRef} disabled={sending} />
            <UploadBtn icon={<Video     size={16} />} title="Record a video"                       inputRef={videoInputRef}  disabled={sending} />
          </div>
          <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
            {showEmojiPicker && (
              <div ref={emojiPickerRef} className="chat-emoji-picker-wrapper">
                <React.Suspense fallback={<div className="chat-emoji-picker-fallback">Loading emoji...</div>}>
                  <EmojiPicker
                    onEmojiClick={handleEmojiClick}
                    theme={document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light"}
                    previewConfig={{ showPreview: false }}
                    skinTonesDisabled={true}
                  />
                </React.Suspense>
              </div>
            )}
            <textarea
              rows={1}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                if (e.target.value) broadcastTyping();
              }}
              onKeyDown={handleKeyDown}
              placeholder={pendingFile ? "Add a caption… (optional)" : "Type a message…"}
              disabled={sending}
              className="chat-textarea"
              style={{ paddingRight: "36px" }}
            />
            <button
              type="button"
              title="Insert emoji"
              aria-label="Insert emoji"
              onClick={() => setShowEmojiPicker((v) => !v)}
              disabled={sending}
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                fontSize: "1.1rem",
                cursor: sending ? "not-allowed" : "pointer",
                zIndex: 1,
                padding: "2px",
                opacity: sending ? 0.4 : 1,
                lineHeight: 1,
              }}
            >
              😊
            </button>
          </div>
          <button
            type="button"
            onClick={sendMessage}
            disabled={!canSend}
            title={!businessId ? "Loading conversation…" : undefined}
            className="chat-send-btn"
          >
            {sending ? "…" : "Send"}
          </button>
        </div>
        )}
      </div>

      {/* Lightbox — inlined so onAddToMedia is directly in scope, no prop-passing */}
      {lightboxItem && (() => {
        const lbItem = lightboxItem;
        const lbClose = () => setLightboxItem(null);
        const lbIsVideo = lbItem.file_type?.startsWith("video/");
        return (
          <div
            onClick={lbClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 10100,
              background: "rgba(0,0,0,0.88)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <button
              onClick={lbClose}
              style={{
                position: "absolute",
                top: "16px",
                right: "20px",
                background: "none",
                border: "none",
                color: "#fff",
                fontSize: "2rem",
                lineHeight: 1,
                cursor: "pointer",
                zIndex: 1,
              }}
              aria-label="Close"
            >
              ×
            </button>

            {lbIsVideo ? (
              <video
                controls
                src={lbItem.file_url}
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: "90vw", maxHeight: "78vh", borderRadius: "8px", boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}
              />
            ) : (
              <img
                src={lbItem.file_url}
                alt={lbItem.file_name || "Image"}
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: "90vw", maxHeight: "78vh", borderRadius: "8px", objectFit: "contain", boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}
              />
            )}

            {typeof onAddToMedia === "function" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToMedia({
                    file_url:    lbItem.file_url,
                    file_name:   lbItem.file_name,
                    file_type:   lbItem.file_type,
                    file_size:   lbItem.file_size,
                    sender_name: lbItem.sender_name,
                    created_at:  lbItem.created_at,
                  });
                  lbClose();
                }}
                style={{
                  background: "var(--primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  padding: "11px 28px",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  boxShadow: "0 4px 16px rgba(255, 138, 0,0.4)",
                }}
              >
                ＋ Add to Media tab
              </button>
            )}
          </div>
        );
      })()}
    </>
  );
}
